import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setUnitId } from '@/lib/slices/unit-slice';
import { setSubunitData, setSubunitId } from '@/lib/slices/subunit-slice';
import {
  setActivityDescription,
  setActivityId,
  setActivityName,
  setActivityType,
  setActivityURL,
} from '@/lib/slices/activity-slice';

import Button from '@/common/components/ui/Button';

import CourseTraversalService from '@/services/course/lesson/course-traversal-service';
import UtilityService from '@/services/util/utility-service';
import ActiveLessonCacheService from '@/services/storage/active-lesson-cache-service';
import {
  ActivityLocation,
  CourseData,
  SubunitData,
  SubunitId,
  UnitData,
  UnitId,
} from 'course-types';
import { LessonStateCookie } from 'lesson-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { usePathname } from 'next/navigation';

interface Props {
  onBack?: () => void;
}

export default function ActivityNavButtons({ onBack }: Props) {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  // TODO:: remove once sidenav implemented
  const pathName = usePathname();
  const isOnAdminCourses = pathName.includes('/admin/courses');

  const activityId: string | undefined = useAppSelector(() => store.getState().activity.id);
  const subunitId: SubunitId | undefined = useAppSelector(() => store.getState().subunit.id);
  const unitId: UnitId = useAppSelector(() => store.getState().unit.id);
  const course: CourseData = useAppSelector(() => store.getState().course.activeCourseData);
  const darkMode = useAppSelector(() => store.getState().navigation.darkMode);

  // Get sidebar state for responsive width
  const sidebarVisible = useAppSelector(() => store.getState().navigation.visible);
  const sidebarMinimized = useAppSelector(() => store.getState().navigation.minimized);

  const [previousActivity, setPreviousActivity] = useState<ActivityLocation | null | undefined>(
    undefined,
  );
  const [nextActivity, setNextActivity] = useState<ActivityLocation | null | undefined>(undefined);
  const [activityPosition, setActivityPosition] = useState<{
    currentIndex: number;
    totalActivities: number;
  } | null>(null);

  // Attempts to locate previous / next activities in relation to current video
  useEffect(() => {
    if (activityId && course && unitId && unitId != ('' as UnitId)) {
      const foundLocations = CourseTraversalService.getAdjacentActivities(
        course,
        unitId,
        subunitId,
        activityId,
      );
      setPreviousActivity(foundLocations.previous);
      setNextActivity(foundLocations.next);

      // Get current activity position
      const position = CourseTraversalService.getCurrentActivityIndex(course, activityId);
      setActivityPosition(position);
    }
  }, [course, activityId]);

  const selectActivity = async (location: ActivityLocation) => {
    // Retrieve unit info
    const unitData: UnitData = course.units[location.unitId];

    // Retrieve subunit info
    let subunitData: SubunitData | undefined = undefined;
    if (location.subunitId) {
      subunitData = (unitData?.subunits ?? {})[location.subunitId];
    }

    // Set unit info
    dispatch(setUnitId(location.unitId ?? undefined));

    // Set subunit info
    if (subunitData) {
      dispatch(setSubunitData({ ...subunitData, id: location.subunitId as string }));
    } else {
      dispatch(setSubunitId(undefined));
    }

    // set activity info
    dispatch(setActivityId(location.activity.id));
    dispatch(setActivityType(location.activity.type));
    dispatch(setActivityName(location.activity.name));
    dispatch(setActivityDescription(location.activity.description));

    if (location.activity.type == 'video' || location.activity.type == 'resource') {
      dispatch(setActivityURL(location.activity.url ?? ''));
    }

    const lessonState: LessonStateCookie = {
      activityName: location.activity.name,
      activityDescription: location.activity.description,
      activityURL: location.activity.url,
      activityId: location.activity.id,
      activityType: location.activity.type,
      subunitName: subunitData?.name ?? '',
      subunitId: location.subunitId ?? '',
      unitName: unitData.name,
      unitId: location.unitId,
      courseName: course.name,
      courseId: course.id,
    };
    ActiveLessonCacheService.setLessonStateCookie(lessonState);
  };

  // Calculate dynamic positioning based on sidebar state
  const getPositionClasses = () => {
    if (sidebarVisible && !sidebarMinimized) {
      return 'left-[260px] w-[calc(100%-260px)]'; // Sidebar is open (260px width)
    } else {
      return 'left-0 md:left-[64px] w-[100%] md:w-[calc(100%-64px)]'; // Sidebar is collapsed (64px width)
    }
  };

  // Create dynamic gradient based on branding colors and dark mode
  const getGradientStyle = (): React.CSSProperties => {
    return UtilityService.getGradientStyle(darkMode);
  };

  return (
    <>
      <div
        className={`fixed bottom-0 z-40 flex justify-between py-[16px] px-[32px] h-[68px] border-t border-border_gray_300 transition-all duration-300 ${getPositionClasses()}`}
        style={getGradientStyle()}
      >
        <Button
          onClick={previousActivity ? () => selectActivity(previousActivity) : undefined}
          disabled={previousActivity === null || previousActivity === undefined}
          outline={true}
          className='h-9 text-sm gap-2 text-primary border-primary'
        >
          <ChevronLeft /> Previous
        </Button>
        <div className='flex flex-row gap-4'>
          <div className='flex items-center font-medium text-text_gray_900'>
            {activityPosition
              ? `Activity ${activityPosition.currentIndex} of ${activityPosition.totalActivities}`
              : null}
          </div>
          {/* TODO:: remove once sidenav implemented */}
          {isOnAdminCourses && (
            <Button
              onClick={onBack}
              outline={true}
              className='h-9 text-sm gap-2 text-primary border-primary'
            >
              Back to Overview
            </Button>
          )}
        </div>
        <Button
          onClick={nextActivity ? () => selectActivity(nextActivity) : undefined}
          disabled={nextActivity === null || nextActivity === undefined}
          outline={true}
          className='h-9 text-sm gap-2 text-primary border-primary'
        >
          Next <ChevronRight />
        </Button>
      </div>
    </>
  );
}
